import type { LogoutHandler } from '@luna/api';
import { HTTP_STATUS_CODE, initRequest } from '@luna/api';
import { getToken, logout } from '@shared/auth';
import { getFeatureConfig } from '@luna/feature-config';
import { getEnv } from '@luna/env';
import { georgeStats } from '@shared/statistics';
import _ from '@luna/lodash';
import { getCurrentLanguage } from '@luna/i18n';
import { uuid } from '@luna/utils';
import {
    addAlexProxyUrl,
    isAlexProxyActive,
    isFatEnvironment,
    isProdEnvironment,
} from '@shared/environment';
import type { LogoutType } from '@luna/utils-ts';
import { isFeatureEnabled } from '@luna/gatekeeper';
import { fetchAndSaveRefreshToken, getAuthData } from '@luna/oidc';
import type { RetailEnvironmentConfig } from '@retail/core-config';

import { setRetailToken } from '../auth/TokenService';
import { updateProxyUrlBasedOnEnvironment } from './reverseProxy';
import {
    ACCEPT_LANGUAGE_HEADER,
    DEFAULT_ACCEPTED_LANGUAGE,
    OCP_APIM_SUBSCRIPTION_KEY,
    X_GEORGE_SCOPE_KEY,
} from '../ajax/AjaxService';

const API_ERROR_TYPE = 'ERROR';
const API_ERROR_NAME = 'ERROR_API';

function isUrlPrestoOrGAPI(url: string): boolean {
    const { prestoUrl, georgeApiUrl } = getEnv();

    return url.indexOf(prestoUrl) > -1 || url.indexOf(georgeApiUrl) > -1;
}

function appendHeaders(url: string): Record<string, string> {
    const headers: Record<string, string> = {};

    const georgeScopeHeader = getFeatureConfig((f) => f.base.headers.georgeScopeHeader);
    if (georgeScopeHeader) {
        headers[X_GEORGE_SCOPE_KEY] = georgeScopeHeader;
    }

    if (getFeatureConfig((f) => f.base.sendLanguageHeader)) {
        headers[ACCEPT_LANGUAGE_HEADER] = _.escape(
            getCurrentLanguage() ?? DEFAULT_ACCEPTED_LANGUAGE,
        );
    }

    if (getFeatureConfig((f) => f.base.sendRequestId) && isUrlPrestoOrGAPI(url)) {
        headers['X-REQUEST-ID'] = uuid();
    }

    if (
        getEnv().webAPIClientId &&
        (url.indexOf('/webapi') > -1 || url.indexOf('/geapi') > -1 || url.indexOf('/gebof') > -1)
    ) {
        headers['WEB-API-key'] = getEnv().webAPIClientId;
    }

    const { apimSubscriptionId, azureApiHost } = getEnv<RetailEnvironmentConfig>();

    if (apimSubscriptionId && azureApiHost && url.indexOf(azureApiHost) > -1) {
        headers[OCP_APIM_SUBSCRIPTION_KEY] = apimSubscriptionId;
    }

    return headers;
}

export function mutateUrl(url: string): string {
    let updatedUrl = url;
    if (
        updatedUrl.indexOf(getEnv().prestoUrl) > -1 &&
        !isProdEnvironment() &&
        !isFatEnvironment() &&
        getFeatureConfig((config) => config.base.enableTraceWebapi)
    ) {
        if (updatedUrl.indexOf('?') === -1) {
            updatedUrl += '?enableTrace=true';
        } else {
            updatedUrl += '&enableTrace=true';
        }
    }

    if (isFeatureEnabled((f) => f.removeNetbankingFromUrl) && url.indexOf('/netbanking/') > -1) {
        updatedUrl = updatedUrl.replace('/netbanking/', '/');
    }

    if (isAlexProxyActive()) {
        return addAlexProxyUrl(url);
    }

    return updateProxyUrlBasedOnEnvironment(updatedUrl);
}

const handleLogout: LogoutHandler = (logoutType, { url }) => {
    if (logoutType === 'http401' || logoutType === 'http403') {
        const unauthorizedStatusCode = getFeatureConfig(
            (config) => config.base.unauthorizedStatusCode,
        );
        const unauthorizedStatusCodeToType: LogoutType =
            unauthorizedStatusCode === '401' ? 'http401' : 'http403';
        // status code does not match unauthorizedStatusCode, or it is not presto/gapi url
        if (!isUrlPrestoOrGAPI(url) || logoutType !== unauthorizedStatusCodeToType) {
            georgeStats.trackEvent(API_ERROR_TYPE, API_ERROR_NAME, {
                url,
                type: logoutType,
            });
            return;
        }
    }
    logout(logoutType);
};

export const initRetailRequest = (): void => {
    initRequest({
        mutateUrl,
        logout: handleLogout,
        appendHeaders,
        authToken: () => getToken(),
        refreshToken: async () => {
            const { refreshToken } = getAuthData();

            if (!refreshToken) {
                return undefined;
            }
            const data = await fetchAndSaveRefreshToken(getEnv(), refreshToken);
            setRetailToken(data.access_token);
            return data.access_token;
        },
        onError: (error) => {
            if (error.status === HTTP_STATUS_CODE.FORBIDDEN) {
                handleLogout('http403', error);
            }
        },
        includeCredentials: getFeatureConfig((c) => c.base.passCredentialsInRequest),
    });
};
